import { useMemo, useState } from "react";
import { AdminTableSkeleton } from "../components/admin/AdminTableSkeleton";
import { useAdminUsers } from "../hooks/useAdminUsers";
import {
  useAdminToolUsage,
  type AdminToolUsage as AdminToolUsageRow,
  type AdminToolUsageFilter,
} from "../hooks/useAdminToolUsage";
import { ADMIN_TOOL_USAGE_RESOURCE, lastThirtyDayWindow } from "../lib/adminQueries";
import { providerLabel } from "../lib/providerLabel";
import styles from "./AdminToolUsage.module.css";
import { AdminPageIntro } from "../components/admin/AdminPageIntro";

// Quick-range presets. Each returns a fresh [start, end) window relative to
// "now" so the underlying summary buckets line up with date_trunc('hour', ...).
type Preset = { id: string; label: string; window: () => { start: Date; end: Date } };

const startOfHour = (d: Date): Date => {
  const x = new Date(d);
  x.setMinutes(0, 0, 0);
  return x;
};

const startOfDay = (d: Date): Date => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

const startOfMonth = (d: Date): Date => {
  const x = new Date(d);
  x.setDate(1);
  x.setHours(0, 0, 0, 0);
  return x;
};

const PRESETS: ReadonlyArray<Preset> = [
  {
    id: "last-24h",
    label: "Last 24h",
    window: () => {
      const end = startOfHour(new Date());
      end.setHours(end.getHours() + 1);
      const start = new Date(end);
      start.setHours(start.getHours() - 24);
      return { start, end };
    },
  },
  {
    id: "last-7d",
    label: "Last 7 days",
    window: () => {
      const end = startOfDay(new Date());
      end.setDate(end.getDate() + 1);
      const start = new Date(end);
      start.setDate(start.getDate() - 7);
      return { start, end };
    },
  },
  {
    id: "last-30d",
    label: "Last 30 days",
    window: () => lastThirtyDayWindow(new Date()),
  },
  {
    id: "this-month",
    label: "This month",
    window: () => {
      const start = startOfMonth(new Date());
      const end = new Date(start);
      end.setMonth(end.getMonth() + 1);
      return { start, end };
    },
  },
];

// Providers the Usage tab can filter to: the values the gateway writes to
// mcp_tool_calls.provider (WhatsApp is a Composio chat channel, so its calls are
// attributed provider="composio"). The filterable set is local; labels come from
// the shared providerLabel() so they match the other admin surfaces.
const PROVIDER_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  "composio",
  "servicem8",
  "simpro",
].map((value) => ({ value, label: providerLabel(value) }));

const formatNumber = (n: number): string => n.toLocaleString();

// Genuine failures exclude locally-rejected (unconnected) calls, which are a
// subset of errorCount but never reached a provider — counting them as errors
// would show a brand-new tenant (nothing connected yet) at 100% error. Clamped
// at 0: the error >= unconnected invariant holds today, but a clamp keeps the
// UI honest (never "-2 failed") if upstream data ever drifts.
const genuineFailures = (row: AdminToolUsageRow): number =>
  Math.max(0, row.errorCount - (row.unconnectedCount ?? 0));

const formatErrorRate = (row: AdminToolUsageRow): string => {
  const forwarded = row.totalCount - (row.unconnectedCount ?? 0);
  if (forwarded <= 0) return "—";
  const failed = genuineFailures(row);
  const pct = (failed / forwarded) * 100;
  if (pct < 0.1 && failed > 0) return "<0.1%";
  return `${pct.toFixed(1)}%`;
};

// Compact range label that keeps the toolbar on one line. Drops the year (admin
// windows are always recent) and shows the time component only for sub-day
// windows — i.e. the hourly "Last 24h" preset. Keyed off the window SPAN, not
// the endpoints' clock values, so a day-aligned preset that happens to start on
// the hour (or a 24h window landing on midnight) is classified correctly.
const DAY_MS = 24 * 60 * 60 * 1000;
const formatRangeLabel = (start: Date, end: Date): string => {
  const withTime = end.getTime() - start.getTime() <= DAY_MS;
  const fmt: Intl.DateTimeFormatOptions = withTime
    ? { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }
    : { month: "short", day: "numeric" };
  return `${start.toLocaleString(undefined, fmt)} → ${end.toLocaleString(undefined, fmt)}`;
};

export const AdminToolUsage = (): JSX.Element => {
  const { users } = useAdminUsers();
  const [presetId, setPresetId] = useState<string>("last-30d");
  const [selectedCustomer, setSelectedCustomer] = useState<string>("");
  const [selectedProvider, setSelectedProvider] = useState<string>("");
  // Bumping windowKey forces the relative-time presets ("Last 24h", etc.)
  // to recompute against the current clock. The preset itself returns a
  // fresh window each call; the useMemo cache would otherwise pin the
  // window endpoints at first-render time and Refresh would re-query the
  // same stale range.
  const [windowKey, setWindowKey] = useState<number>(0);

  // Recompute the window when the preset changes OR the user asks for fresh
  // data. (Custom range is a follow-up; for V1 we only expose the presets.)
  const window = useMemo(() => {
    const preset = PRESETS.find((p) => p.id === presetId) ?? PRESETS[2]!;
    return preset.window();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetId, windowKey]);

  const filter: AdminToolUsageFilter = useMemo(
    () => ({
      start: window.start,
      end: window.end,
      customerEmail: selectedCustomer || undefined,
      provider: selectedProvider || undefined,
    }),
    [window, selectedCustomer, selectedProvider],
  );

  const isDefaultView =
    presetId === "last-30d" && selectedCustomer === "" && selectedProvider === "";
  const { status, rows, error, isFetching, refresh } = useAdminToolUsage(
    filter,
    isDefaultView ? ADMIN_TOOL_USAGE_RESOURCE : null,
  );

  const sortedUsers = useMemo(
    () => users.slice().sort((a, b) => a.email.localeCompare(b.email)),
    [users],
  );

  const totals = useMemo(() => {
    let total = 0;
    let success = 0;
    let error = 0;
    let unconnected = 0;
    for (const r of rows) {
      total += r.totalCount;
      success += r.successCount;
      error += r.errorCount;
      unconnected += r.unconnectedCount ?? 0;
    }
    // IP-314: unconnected calls are a subset of errors but were never forwarded.
    // Partition cleanly: failed (genuine) = error - unconnected, and
    // forwarded = total - unconnected, so success + failed + unconnected = total.
    // Clamped at 0 to stay honest if the error >= unconnected invariant drifts.
    return {
      total,
      success,
      failed: Math.max(0, error - unconnected),
      unconnected,
      forwarded: Math.max(0, total - unconnected),
    };
  }, [rows]);

  return (
    <div className={styles.page}>
      <AdminPageIntro title="Usage">
        Tool calls per customer over the chosen window. A multi-execute batch counts as each of its sub-tools.
      </AdminPageIntro>

      <section className={styles.toolbar}>
        <div className={styles.filterRow}>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Customer</span>
            <select
              value={selectedCustomer}
              onChange={(e) => setSelectedCustomer(e.target.value)}
            >
              <option value="">All customers</option>
              {sortedUsers.map((u) => (
                <option key={u.email} value={u.email}>
                  {u.email}
                </option>
              ))}
            </select>
          </label>

          <label className={styles.field}>
            <span className={styles.fieldLabel}>Provider</span>
            <select
              value={selectedProvider}
              onChange={(e) => setSelectedProvider(e.target.value)}
            >
              <option value="">All providers</option>
              {PROVIDER_OPTIONS.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className={styles.rangeRow}>
          <div className={styles.chipGroup} role="group" aria-label="Date range">
            {PRESETS.map((preset) => (
              <button
                key={preset.id}
                type="button"
                className={`${styles.chip} ${presetId === preset.id ? styles.chipActive : ""}`}
                onClick={() => setPresetId(preset.id)}
              >
                {preset.label}
              </button>
            ))}
          </div>

          <div className={styles.toolbarRight}>
            <span className={styles.rangeLabel}>{formatRangeLabel(window.start, window.end)}</span>
            <button
              type="button"
              className={styles.refreshBtn}
              onClick={() => {
                setWindowKey((k) => k + 1);
                refresh();
              }}
            >
              Refresh
            </button>
          </div>
        </div>
      </section>

      {status === "ready" && rows.length > 0 && (
        <section className={styles.totalsStrip}>
          <div className={styles.totalCard}>
            <span className={styles.totalLabel}>Total calls</span>
            <span className={styles.totalValue}>{formatNumber(totals.total)}</span>
          </div>
          <div className={styles.totalCard}>
            <span className={styles.totalLabel}>Sent to provider</span>
            <span className={styles.totalValue}>{formatNumber(totals.forwarded)}</span>
          </div>
          <div className={styles.totalCard}>
            <span className={styles.totalLabel}>Skipped</span>
            <span className={`${styles.totalValue} ${styles.totalUnconnected}`}>
              {formatNumber(totals.unconnected)}
            </span>
          </div>
          <div className={styles.totalCard}>
            <span className={styles.totalLabel}>Successful</span>
            <span className={`${styles.totalValue} ${styles.totalSuccess}`}>{formatNumber(totals.success)}</span>
          </div>
          <div className={styles.totalCard}>
            <span className={styles.totalLabel}>Failed</span>
            <span className={`${styles.totalValue} ${styles.totalError}`}>{formatNumber(totals.failed)}</span>
          </div>
          <div className={styles.totalCard}>
            <span className={styles.totalLabel}>Customers</span>
            <span className={styles.totalValue}>{formatNumber(rows.length)}</span>
          </div>
        </section>
      )}

      <section className={styles.listSection}>
        {status === "loading" && (
          <AdminTableSkeleton
            headers={["Customer", "Total", "Successful", "Failed", "Skipped", "Error rate", "Tenants"]}
            tableClassName={styles.table ?? ""}
          />
        )}
        {status === "error" && (
          <div className={styles.errorPanel}>
            Couldn't load usage{error ? `: ${error}` : "."}
          </div>
        )}
        {status === "ready" && rows.length === 0 && (
          <div className={styles.empty}>
            No tool calls in this window.
          </div>
        )}
        {status === "ready" && rows.length > 0 && (
          <table
            className={isFetching ? `${styles.table} ${styles.tableFetching}` : styles.table}
          >
            <thead>
              <tr>
                <th>Customer</th>
                <th className={styles.numericCell}>Total</th>
                <th className={styles.numericCell}>Successful</th>
                <th className={styles.numericCell}>Failed</th>
                <th className={styles.numericCell}>Skipped</th>
                <th className={styles.numericCell}>Error rate</th>
                <th className={styles.numericCell}>Tenants</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.ownerEmail}>
                  <td className={styles.email}>{row.ownerEmail}</td>
                  <td className={styles.numericCell}>{formatNumber(row.totalCount)}</td>
                  <td className={`${styles.numericCell} ${styles.success}`}>
                    {formatNumber(row.successCount)}
                  </td>
                  <td
                    className={`${styles.numericCell} ${
                      genuineFailures(row) > 0 ? styles.error : styles.muted
                    }`}
                  >
                    {formatNumber(genuineFailures(row))}
                  </td>
                  <td
                    className={`${styles.numericCell} ${
                      (row.unconnectedCount ?? 0) > 0 ? styles.unconnected : styles.muted
                    }`}
                  >
                    {formatNumber(row.unconnectedCount ?? 0)}
                  </td>
                  <td className={styles.numericCell}>{formatErrorRate(row)}</td>
                  <td className={`${styles.numericCell} ${styles.muted}`}>
                    {formatNumber(row.tenantCount)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
};
